import * as _ from 'underscore';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../_common/platform/ts/Constants';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

export interface IMessage {
    content: string;
    reactContent?;
    count: number;
    details?;
}

export type NotificationType = 'success' | 'fail' | 'warning';

export interface INotification {
    id: number;
    type: NotificationType;
    widgetRunTimeID: number;
    options: I.INotificationOptions;
    messages: IMessage[];
    timeStamp?: number;
}

export interface ILoadingPanel {
    widgetRunTimeID: number;
    options?: ILoadingPanelOptions;
}

export interface ILoadingPanelOptions {
    buttonTitle?: string;
    size?: string;
    canCancelQuery?: boolean;
}

export interface INotificationsState {
    notifications: INotification[];
    loadingPanels: { [widgetRunTimeID: string]: ILoadingPanel };
}

let _id = 0;

const MIN_SHOW_TIME = 5000;

export class NotificationsStore extends BaseStore<INotificationsState, AppActions, Dispatcher, IGlob> {
    public _state: INotificationsState;
    public static _name = 'notifications';

    private _delayedDelete: NodeJS.Timer;

    public static getInitialState() {
        return {
            notifications: [],
            loadingPanels: {}
        };
    }

    private createMessage(message: string, options: I.INotificationOptions): IMessage {
        return {
            content: message,
            reactContent: options.reactContent,
            count: 1,
            details: options.details
        };
    }

    private addNotification(
        type: NotificationType,
        text: string,
        widgetRunTimeID: number,
        options: I.INotificationOptions
    ) {
        if (options == null) {
            options = {};
        }

        const { manualDestroy, progress, remainder } = options;

        if (!progress && text != null && text.trim().length === 0) {
            return;
        }

        const { notifications } = this._state;

        if (widgetRunTimeID === null) {
            widgetRunTimeID = undefined;
        }

        this.dropNotificationDeleteTimer();

        const existingNotification = _.find(notifications, (notification) => {
            return (
                notification.widgetRunTimeID === widgetRunTimeID &&
                notification.type === type &&
                notification.options.manualDestroy === manualDestroy &&
                notification.options.progress === progress &&
                notification.options.remainder === remainder
            );
        });

        if (existingNotification == null) {
            notifications.push({
                id: _id,
                type: type,
                widgetRunTimeID: widgetRunTimeID,
                messages: [this.createMessage(text, options)],
                options: options,
                timeStamp: new Date().getTime()
            });

            _id += 1;
        } else {
            const existingMessage = _.find(existingNotification.messages, (message) => {
                return message.content === text;
            });

            if (existingMessage != null) {
                existingMessage.count += 1;
            } else {
                existingNotification.messages.push(this.createMessage(text, options));
            }

            existingNotification.timeStamp = new Date().getTime();
        }
    }

    private cleanNotification(notificationID: number) {
        this._state.notifications = _.reject(this._state.notifications, ({ id }) => {
            return id === notificationID;
        });
    }

    private dropNotificationDeleteTimer() {
        if (this._delayedDelete != null) {
            clearTimeout(this._delayedDelete);
            this._delayedDelete = null;
        }
    }

    private cleanRemainderMessage(messageID) {
        const notifications = this._state.notifications.filter((n) => {
            return (
                n.options.remainder === true &&
                n.messages.findIndex((m) => {
                    return m.details != null && m.details.id != null && m.details.id === messageID;
                }) !== -1
            );
        });

        notifications.forEach((n) => {
            const messages = n.messages.filter((m) => {
                return !(m.details != null && m.details.id != null && m.details.id === messageID);
            });

            n.messages = messages;
        });

        notifications.forEach((n) => {
            if (n.messages.length === 0) {
                this.cleanNotification(n.id);
            } else {
                const idx = this._state.notifications.findIndex((nn) => {
                    return nn.id === n.id;
                });
                this._state.notifications[idx].messages = n.messages;
            }
        });
    }

    private cleanWidgetNotifications(runTimeWidgetID: number, immediately: boolean) {
        if (this._state.notifications == null || this._state.notifications.length === 0) {
            return;
        }

        const newNotifications: INotification[] = [];

        const runUpdate = () => {
            this._state.notifications = newNotifications;

            this.emitOnInWidgetNotificationEvent();
            this.emitOnStandaloneNotificationEvent();
            this.emitChangeWithoutSaving();
        };

        if (immediately) {
            runUpdate();
            return;
        }

        const now = new Date().getTime();

        let deleteLater = false;

        this._state.notifications.forEach((notification) => {
            if (
                notification.widgetRunTimeID != runTimeWidgetID && //'!=' is correct to make standalong notifications be deleted on runTimeWidgetID == null.
                (runTimeWidgetID != null || !notification.options?.showInPopup)
            ) {
                newNotifications.push(notification);
            } else if (
                notification.type !== 'success' &&
                notification.timeStamp != null &&
                notification.timeStamp + MIN_SHOW_TIME > now
            ) {
                deleteLater = true;
            }
        });

        if (this._state.notifications.length === newNotifications.length) {
            return;
        }

        if (deleteLater) {
            if (runTimeWidgetID != null) {
                console.log(
                    `A notification for widget '${runTimeWidgetID}' is cleaned too early. It means that the widget can have extra refresh(es).`
                );
            }

            this.dropNotificationDeleteTimer();
            this._delayedDelete = setTimeout(runUpdate, MIN_SHOW_TIME);
        } else {
            runUpdate();
        }
    }

    private showLoadingPanel(runTimeWidgetID: number) {
        if (runTimeWidgetID == null) {
            return;
        }

        const key = runTimeWidgetID.toString();

        if (this._state.loadingPanels[key] == null) {
            this._state.loadingPanels[key] = {
                widgetRunTimeID: runTimeWidgetID
            };
        }
    }

    private hideLoadingPanel(runTimeWidgetID: number) {
        if (runTimeWidgetID == null) {
            return;
        }

        const key = runTimeWidgetID.toString();

        if (this._state.loadingPanels[key] != null) {
            delete this._state.loadingPanels[key];
        }
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a: Action = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.NOTIFICATION>(a, Constants.NOTIFICATION_SHOW)) {
                this.addNotification(a.type, a.message, a.runTimeWidgetID, a.options);

                if (a.runTimeWidgetID == null && (a.options == null || a.options.showInPopup !== true)) {
                    this.emitOnStandaloneNotificationEvent();
                } else {
                    this.emitOnInWidgetNotificationEvent();
                }

                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.CLEAN_NOTIFICATION>(a, Constants.NOTIFICATION_CLEAN)) {
                this.cleanNotification(a.id);
                this.emitOnStandaloneNotificationEvent();
                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.CLEAN_WIDGET_NOTIFICATIONS>(a, Constants.CLEAN_WIDGET_NOTIFICATIONS)) {
                this.cleanWidgetNotifications(a.runTimeWidgetID, a.immediately);
            }

            if (_case<I.CLEAN_REMAINDER_MESSAGE>(a, Constants.CLEAN_REMAINDER_MESSAGE)) {
                this.cleanRemainderMessage(a.messageID);

                this.emitOnStandaloneNotificationEvent();
                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.SHOW_LOADING_PANEL>(a, Constants.LOADING_PANEL_SHOW)) {
                this.showLoadingPanel(a.runTimeWidgetID);
                this.emitOnInWidgetNotificationEvent();
                this.emitChangeWithoutSaving();
            }

            if (_case<I.HIDE_LOADING_PANEL>(a, Constants.LOADING_PANEL_HIDE)) {
                this.hideLoadingPanel(a.runTimeWidgetID);
                this.emitOnInWidgetNotificationEvent();
                this.emitChangeWithoutSaving();
            }
        } while (false);

        return true;
    }

    private static ON_STANDALONE_NOTIFICATION_EVENT = 'standalone-notification';
    private static ON_WIDGET_NOTIFICATION_EVENT = 'in-widget-notification';

    public addOnStandaloneNotificationListener(callback: Function) {
        this.on(NotificationsStore.ON_STANDALONE_NOTIFICATION_EVENT, callback);
    }

    public removeOnStandaloneNotificationListener(callback: Function) {
        this.off(NotificationsStore.ON_STANDALONE_NOTIFICATION_EVENT, callback);
    }

    private emitOnStandaloneNotificationEvent() {
        this.trigger(NotificationsStore.ON_STANDALONE_NOTIFICATION_EVENT);
    }

    public addOnInWidgetNotificationListener(callback: Function) {
        this.on(NotificationsStore.ON_WIDGET_NOTIFICATION_EVENT, callback);
    }

    public removeOnInWidgetNotificationListener(callback: Function) {
        this.off(NotificationsStore.ON_WIDGET_NOTIFICATION_EVENT, callback);
    }

    private emitOnInWidgetNotificationEvent() {
        this.trigger(NotificationsStore.ON_WIDGET_NOTIFICATION_EVENT);
    }

    constructor(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, NotificationsStore._name, true);

        this._state = _.extend(NotificationsStore.getInitialState(), this._state);
    }

    static get(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        return new NotificationsStore(glob, dispatcher, actions);
    }
}
